import React from 'react';
import { Calendar, Clock } from 'lucide-react';
import LikeButton from './LikeButton';
import { useArticleHeader } from '../../context/ArticleHeaderContext';

export interface AuthorInfoProps {
  name: string;
  fullName?: string;
  avatar?: string;
  lastUpdated?: string;
  readTime?: string;
  showDate?: boolean;
  showReadTime?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const AuthorInfo: React.FC<AuthorInfoProps> = ({
  name,
  fullName,
  avatar,
  lastUpdated,
  readTime,
  showDate = true,
  showReadTime = true,
  size = 'md',
  className = '',
}) => {
  const articleHeader = useArticleHeader();
  const sizeClasses = {
    sm: {
      avatar: 'w-6 h-6',
      text: 'text-xs',
      icon: 'w-3 h-3',
    },
    md: {
      avatar: 'w-8 h-8',
      text: 'text-sm',
      icon: 'w-4 h-4',
    },
    lg: {
      avatar: 'w-10 h-10',
      text: 'text-base',
      icon: 'w-5 h-5',
    },
  };

  const currentSize = sizeClasses[size];

  const getInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const displayName = fullName || name;
  const initials = getInitials(displayName);

  return (
    <div className={`article-author-info flex flex-wrap items-center gap-4 ${currentSize.text} text-gray-600 dark:text-gray-400 ${className}`}>
      {/* Author with Avatar */}
      <div className="flex items-center gap-2">
        {avatar ? (
          <img
            src={avatar}
            alt={displayName}
            className={`${currentSize.avatar} rounded-full object-cover border-2 border-gray-200 dark:border-gray-700`}
          />
        ) : (
          <div className={`article-author-avatar ${currentSize.avatar} rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold ${currentSize.text} border-2 border-gray-200 dark:border-gray-700`}>
            {initials}
          </div>
        )}
        <div className="flex flex-col">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {displayName}
          </span>
        </div>
      </div>

      {/* Date */}
      {showDate && lastUpdated && (
        <>
          <div className="hidden sm:block w-px h-4 bg-gray-300 dark:bg-gray-600" />
          <div className="flex items-center gap-2">
            <Calendar className={currentSize.icon} />
            <span>Last Updated: {lastUpdated}</span>
          </div>
        </>
      )}

      {/* Read Time */}
      {showReadTime && readTime && (
        <>
          <div className="hidden sm:block w-px h-4 bg-gray-300 dark:bg-gray-600" />
          <div className="flex items-center gap-2">
            <Clock className={currentSize.icon} />
            <span>{readTime}</span>
          </div>
        </>
      )}

      {articleHeader && (
        <LikeButton
          contentPageId={articleHeader.articleId}
          source="article"
          className="language-article-header-like"
        />
      )}
    </div>
  );
};

export default AuthorInfo;
